import { hash01 } from '@/core/rng';
import { weatherAt } from '@/world/weather';
import type { FireSim } from './fire';
import type { EventBus } from './events';

/**
 * Natural ignition: the sky lights its own fires, so a god's do not stand
 * alone (law 10 — if the only fires that ever existed were divine, then
 * "unexplained fire" and "a god did this" are the same sentence, and Strike
 * has nowhere to hide). It also gives succession (§52) its first term: a
 * disturbance the mortal world did not make.
 *
 * The whole system is a pure schedule — a function of (world seed, absolute
 * minute) with NO state of its own, which is why it needs nothing in the
 * snapshot: a restored world computes the same strikes the donor would have.
 * Each minute of world time draws CANDIDATES hashed points over the island's
 * square; each strikes with p = storm × STRIKE_RATE, so bolts fall only where
 * the weather field says a storm is — the same field the renderer draws,
 * which is the deniability: every natural fire starts under a sky any witness
 * could have seen coming.
 *
 * Every constant here was picked off a distribution printed by
 * `tools/lightning.ts`, not felt out. The measured shape (2 years of sky):
 * storm > 0.05 over 8.1% of space-time; wherever it thunders the rain is
 * already ≥ 0.5 (61% of stormy samples at 0.9–1.0), so the (1 − rain)² spark
 * gate passes ~2% of strikes and only at a cell's margins — dry lightning is
 * an edge phenomenon, and most strikes must ignite nothing.
 */

/** Candidate draws per world-minute, each a possible bolt somewhere. */
const CANDIDATES = 2;
/**
 * Strike probability per candidate is storm × this. At 2 candidates/min the
 * probe measured 9.1 strikes/km²·yr on land at rate 1.0; temperate-maritime
 * skies really deliver 0.5–3, so 0.11 lands the island at ~1, mid-range.
 * It opened at 0.22 — with SPARK at 0.9 the first end-to-end year burned
 * the island seven times over, and both halves of that product came down.
 */
const STRIKE_RATE = 0.11;
/**
 * Of the strikes, how many even try to ignite: hard rain refuses a spark
 * quadratically (the probe's histogram is why it must be steep), and what
 * remains is scaled by the fuel actually standing there, so a bolt into
 * sparse or soaked ground is thunder and nothing else. Halved from 0.9 in
 * the same recalibration that halved STRIKE_RATE.
 */
const SPARK = 0.5;

const MINUTE = 60;

export interface Bolt { x: number; z: number; t: number }

/** How far a bolt may land from the cell that made it, metres. */
const BOLT_REACH = 2000;

/**
 * Every bolt the sky delivers in the world-minute ending at `t` (t on the
 * absolute 60 s grid). Pure: same seed, same minute, same bolts, forever —
 * exposed so probes (and one day a renderer) can ask about strikes without
 * running a simulation.
 *
 * A bolt is not obliged to land under the cell that charged it: real
 * cloud-to-ground strikes reach kilometres out of a storm's skirt, and that
 * reach is load-bearing here — the probe measured rain ≥ 0.9 under 61% of
 * stormy sky, so a bolt pinned to its own core would land in water almost
 * every time and the island would starve of fire. Two kilometres off a
 * 14 km feature still keeps every bolt inside weather a witness can see.
 */
export function boltsAtMinute(seed: number, t: number, halfExtent: number): Bolt[] {
  const k = Math.round(t / MINUTE);
  const out: Bolt[] = [];
  for (let i = 0; i < CANDIDATES; i++) {
    const x = (hash01(k, i * 2 + 1, seed ^ 0x11a7) - 0.5) * 2 * halfExtent;
    const z = (hash01(i * 2 + 2, k, seed ^ 0x33b9) - 0.5) * 2 * halfExtent;
    const storm = weatherAt(seed, t, x, z).storm;
    if (storm <= 0) continue;
    if (hash01(k, i + 7, seed ^ 0x77c1) >= storm * STRIKE_RATE) continue;
    const ang = hash01(i + 11, k, seed ^ 0x6f01) * 2 * Math.PI;
    const rad = Math.sqrt(hash01(k, i + 13, seed ^ 0x6f02)) * BOLT_REACH;
    out.push({ x: x + Math.cos(ang) * rad, z: z + Math.sin(ang) * rad, t });
  }
  return out;
}

/**
 * The chance a bolt has anything to say beyond thunder. Split out from the
 * schedule so a probe can integrate it over a season without a FireSim.
 */
export function sparkChance(seed: number, b: Bolt, fuel: number): number {
  const rain = weatherAt(seed, b.t, b.x, b.z).rain;
  return SPARK * (1 - rain) * (1 - rain) * fuel;
}

export class LightningSystem {
  constructor(
    private readonly seed: number,
    private readonly halfExtent: number,
    private readonly fire: FireSim,
    private readonly events: EventBus,
  ) {}

  /**
   * Runs on the scheduler's own 60 s grid, registered ahead of the fire tick
   * so a bolt's cell is alight before the same boundary's spread step. The
   * fire_started it emits is byte-for-byte the shape a divine strike's fire
   * emits — radius, magnitude, salience — with the cause the world can
   * actually see: natural. Anything less identical would hand a witness a
   * way to sort fires into "weather" and "other", and law 10 dies there.
   */
  advance(from: number, to: number): void {
    let k = Math.floor(from / MINUTE) + 1;
    for (let t = k * MINUTE; t <= to; t = ++k * MINUTE) {
      for (const b of boltsAtMinute(this.seed, t, this.halfExtent)) {
        const fuel = this.fire.fuelAtWorld(b.x, b.z, t);
        if (fuel <= 0) continue;
        const p = sparkChance(this.seed, b, fuel);
        const roll = hash01(Math.round(b.x) + k, Math.round(b.z) - k, this.seed ^ 0x51a2);
        if (roll >= p) continue;
        if (!this.fire.ignite(b.x, b.z, t)) continue;
        const fs = this.events.emit({
          type: 'fire_started', time: t, x: b.x, z: b.z,
          radius: 200, magnitude: 1, salience: 0.8,
          cause: { kind: 'natural' },
        });
        // the sky's fires are credited to the sky and to nobody — but the burn
        // still needs its parent, or a god who later feeds this same fire
        // would inherit a chain with a missing first link
        this.fire.creditBurn(fs.id);
      }
    }
  }
}
